import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link, useHistory } from 'react-router-dom';

import { Check, ChevronRight, X } from 'lucide-react';

import { sizes } from '../../theme/device';

import { classes } from '../../components/MainContent/classes';

import { Button } from '../../components-ui/Button';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '../../components-ui/Popover';
import { useSidebar } from '../../context/SidebarProvider';
import { useUnsavedChanges } from '../../context/UnsavedChangesProvider';
import { FOCUS_RING } from '../../lib/focusRing';
import { cn } from '../../lib/utils';
import useChecklistAnnouncerStore from '../../store/checklist-announcer';
import useOverlayStore from '../../store/overlay';
import { rowAction, type StepTurn, stepTurn } from './rowAction';
import type { ChecklistStep, StepId } from './steps';
import { useStarterChecklist } from './useStarterChecklist';

const STEP_LABEL: Record<StepId, string> = {
  'connect-channel': 'Connect a channel',
  'create-portfolio': 'Create a portfolio',
  'add-content': 'Add contents',
};

/**
 * One line each, describing what the step gives you. Deliberately free of optimisation
 * language — this tier has bought `performance.view` and `budget.view` and nothing else — and
 * free of plurals the data never guarantees: one channel and one content tick their steps.
 */
const STEP_SUBTITLE: Record<StepId, string> = {
  'connect-channel': 'Connect an ad account or analytics integration',
  'create-portfolio': 'Group the channels you track together',
  'add-content': 'Choose what the portfolio should track',
};

/**
 * Neither colour nor position is the state. This is — read out next to the aria-hidden marker,
 * so the run order survives for a reader who never sees the spine or the ring.
 */
const TURN_TEXT: Record<StepTurn, string> = {
  done: 'Done',
  next: 'Do this next',
  later: 'Later in the sequence',
};

/**
 * A waiting row names the step directly above it, so it states the ORDER rather than an errand.
 * `connect-channel` has no entry because it is always first and therefore never waits.
 */
const STEP_PREREQUISITE: Partial<Record<StepId, string>> = {
  'create-portfolio': 'After you connect a channel',
  'add-content': 'After you create a portfolio',
};

/**
 * TWO markers, not four: a step is finished or it is not. Green says finished; every pending
 * step wears the same dashed grey ring, so the panel never spends the finished colour — or an
 * ink ring loud enough to be mistaken for one — on unfinished work.
 */
const PENDING_MARKER = 'border-dashed border-neutral-300 bg-white text-neutral-500';

/**
 * One entry per place in the run, so the emphases cannot drift apart. Separate `turn === 'next'`
 * tests scattered down the row are separate chances to wire one backwards, and nothing
 * user-visible would fail if one were. With the markers unified, the turn is carried by the
 * label weight, the darker chevron and being the panel's only link.
 */
const TURN_STYLE: Record<StepTurn, { marker: string; label: string; subtitle: string }> = {
  done: {
    marker: 'border-green-400 bg-green-400 text-white',
    label: 'text-neutral-500',
    subtitle: 'text-neutral-500',
  },
  next: {
    marker: PENDING_MARKER,
    label: 'font-medium text-neutral-900',
    subtitle: 'text-neutral-600',
  },
  later: {
    marker: PENDING_MARKER,
    label: 'text-neutral-700',
    subtitle: 'text-neutral-500',
  },
};

/**
 * ONE hover, panel-wide. The turn row used to carry a resting fill and a darker hover of its
 * own; two hover treatments in a four-row panel read as two kinds of control. The turn is
 * already the only link here, and the ink ring plus the label weight still mark it.
 */
const ROW_HOVER = 'hover:bg-neutral-50';

/** Waiting on the one real dependency, which outranks its place in the run. */
const BLOCKED_STYLE = {
  marker: PENDING_MARKER,
  label: 'text-neutral-500',
};

/** The event, not the count: "3 of 3" alone does not say what just changed. */
const STEP_DONE_ANNOUNCEMENT: Record<StepId, string> = {
  'connect-channel': 'Channel connected.',
  'create-portfolio': 'Portfolio created.',
  'add-content': 'Contents added.',
};

const HEADING_ID = 'setup-checklist-heading';
const TOTAL_STEPS = 3;

const triggerLabel = (doneCount: number) => `Get started, ${doneCount} of ${TOTAL_STEPS} steps done`;

/**
 * The Nexoya Starter setup checklist: a progress card in the sidebar rail that opens the
 * steps in a panel beside it.
 *
 * The rail never holds the steps themselves at any width — it holds a count and a
 * three-segment meter, and the meter is the same instrument the icon-only rail draws as a
 * ring and the panel repeats above the rows.
 */
export function SetupChecklistSection(): JSX.Element | null {
  const { enabled, checklist, portfolioId, retire } = useStarterChecklist();
  const { isCollapsed } = useSidebar();
  // `SidebarProvider` forces `isCollapsed` from an effect, so there is one mount frame below
  // 1440 where it is still false — the width query is what covers it.
  const isLaptopLUp = useMediaQuery({ minWidth: sizes.laptopL });
  // Incremented from `Modal`'s mount effect, so one wiring point covers every legacy modal.
  const overlayOpen = useOverlayStore((state) => state.open) > 0;
  const announce = useChecklistAnnouncerStore((state) => state.announce);
  const [open, setOpen] = React.useState(false);
  const settledRef = React.useRef(false);
  const previousStepsRef = React.useRef<readonly ChecklistStep[] | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  // Tracked from focus/blur rather than read off the DOM in the effect: by the time the effect
  // runs the completed row's `<Link>` has already unmounted, taking focus to `<body>` with it.
  const focusWithinRef = React.useRef(false);

  /**
   * The ONLY unmount path, and the order is load-bearing: `.NEXYMainContent` takes focus
   * FIRST, then the retirement latch lands. Nothing traps focus in this tree, so unmounting
   * with focus inside would drop it to `<body>` with no recovery.
   */
  const hide = React.useCallback(() => {
    const main = document.querySelector<HTMLElement>(`.${classes.root}`);
    if (main) {
      main.tabIndex = -1;
      main.focus();
    }
    retire();
  }, [retire]);

  // `enabled` carries plan, teamId, `portfolio.create` and the retirement latch. Unresolved is
  // a distinct state, not "zero done": the integrations query is never served from cache, so
  // without it the card flashes "0 of 3" on every hard load and every team switch.
  const rendered = enabled && checklist.resolved;

  React.useEffect(() => {
    // Drop the baseline while suppressed, so a step that completes off-screen is not announced
    // late when the card comes back.
    if (!rendered) {
      previousStepsRef.current = null;
      return;
    }

    const previous = previousStepsRef.current;
    previousStepsRef.current = checklist.steps;

    // The first resolve is the page load, not an event.
    if (!settledRef.current) {
      settledRef.current = true;
      return;
    }
    if (!previous) return;

    const was = (id: StepId) => previous.find((earlier) => earlier.id === id)?.status;

    const completed = checklist.steps.find((step) => step.status === 'done' && was(step.id) !== 'done');
    if (completed) {
      announce(
        `${STEP_DONE_ANNOUNCEMENT[completed.id]} Get started: ${checklist.doneCount} of ${TOTAL_STEPS} steps done.`,
      );
    }

    /**
     * A row drops its own `<Link>` by COMPLETING or by becoming BLOCKED — a disconnect regresses
     * step 1, which re-gates step 2 under the user's cursor. So this keys off focus landing on
     * `<body>`, not off completion: removing the focused node fires no `focusout`, so nothing
     * else in this tree can notice. The poll makes both reachable while the tab is in front of
     * you. Radix cannot cover it either — this popover is non-modal, so `FocusScope` is untrapped.
     */
    if (focusWithinRef.current && document.activeElement === document.body) {
      (closeRef.current ?? triggerRef.current)?.focus();
    }
  }, [announce, checklist.doneCount, checklist.steps, rendered]);

  // The panel is PORTALED, so `hidden` on the rail wrapper cannot reach it, and it sits at
  // z-[3500] — above the legacy Modal's 1300 it would otherwise cover.
  React.useEffect(() => {
    if (overlayOpen) setOpen(false);
  }, [overlayOpen]);

  // `rendered` drops the tree, not this component (the Sidebar mounts it unconditionally), so
  // `open` outlived it: retiring the card with the panel up and then switching to a team that
  // has not retired it brought the panel back open, with Radix pulling focus into it.
  React.useEffect(() => {
    if (!rendered) setOpen(false);
  }, [rendered]);

  if (!rendered) return null;

  const compact = isCollapsed || !isLaptopLUp;

  return (
    // A plain block div: `hidden` is `display: none`, which any flex/grid utility would beat.
    <div
      data-testid="setup-checklist"
      hidden={overlayOpen}
      onFocus={() => {
        focusWithinRef.current = true;
      }}
      onBlur={() => {
        focusWithinRef.current = false;
      }}
      className={cn('mb-1', compact ? 'px-2' : 'px-3')}
    >
      <Popover open={open} onOpenChange={setOpen}>
        {compact ? (
          <div className="flex justify-center">
            <PopoverTrigger asChild>
              <Button
                ref={triggerRef}
                variant="ghost"
                data-testid="setup-checklist-toggle"
                aria-label={triggerLabel(checklist.doneCount)}
                className={cn(
                  'h-10 w-10 rounded-md p-0 ring-offset-0 hover:bg-[#f4f4f6] data-[state=open]:bg-[#f2f2f4]',
                  FOCUS_RING,
                )}
              >
                <SegmentedRing doneCount={checklist.doneCount} allComplete={checklist.allComplete} />
              </Button>
            </PopoverTrigger>
          </div>
        ) : (
          <PopoverTrigger asChild>
            <Button
              ref={triggerRef}
              variant="ghost"
              data-testid="setup-checklist-toggle"
              aria-label={triggerLabel(checklist.doneCount)}
              className={cn(
                // Outlined, not filled: the Teams card above is the rail's white card, so this
                // borrows its border and sits a shade quieter on the same seasalt rail.
                'flex h-auto w-full flex-col items-stretch justify-start gap-2 whitespace-normal rounded-md border border-neutral-100 bg-neutral-50 px-2.5 py-2 text-left text-neutral-700 ring-offset-0 hover:bg-[#f4f4f6] hover:text-neutral-700 data-[state=open]:bg-[#f2f2f4]',
                FOCUS_RING,
              )}
            >
              <span className="flex items-baseline gap-2">
                <span aria-hidden className="min-w-0 flex-1 truncate text-[13px] font-medium leading-none">
                  Get started
                </span>
                <span aria-hidden className="shrink-0 text-[11px] tabular-nums text-neutral-500">
                  {checklist.doneCount} of {TOTAL_STEPS}
                </span>
              </span>
              <Meter doneCount={checklist.doneCount} testId="setup-checklist-meter" />
            </Button>
          </PopoverTrigger>
        )}

        <PopoverContent
          ref={contentRef}
          side="right"
          align="end"
          sideOffset={12}
          collisionPadding={8}
          tabIndex={-1}
          aria-labelledby={HEADING_ID}
          data-testid="setup-checklist-popover"
          // Focus the panel itself, not its first tabbable — which is the close button, and
          // opening a checklist on its own dismiss control is the wrong first offer.
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            contentRef.current?.focus();
          }}
          // Surface tokens copied from `HoverCard`, the design system's white floating panel.
          className="w-[min(20rem,calc(100vw-96px))] rounded-lg border border-neutral-100 bg-white p-0 text-neutral-900 shadow-[0px_16px_24px_0px_rgba(19,19,20,0.11)]"
        >
          <div className="flex items-center gap-2 px-3 pt-3">
            <h2 id={HEADING_ID} className="min-w-0 flex-1 text-[13px] font-medium leading-none text-neutral-900">
              Get started
            </h2>
            <PopoverClose asChild>
              <Button
                ref={closeRef}
                variant="ghost"
                aria-label="Close"
                data-testid="setup-checklist-close"
                className={cn(
                  'h-6 w-6 shrink-0 rounded-md p-0 text-neutral-500 ring-offset-0 hover:bg-neutral-50 hover:text-neutral-900',
                  FOCUS_RING,
                )}
              >
                <X aria-hidden className="h-3.5 w-3.5" />
              </Button>
            </PopoverClose>
          </div>

          <div className="flex items-center gap-3 px-3 pt-2.5">
            <Meter doneCount={checklist.doneCount} className="flex-1" />
            <span className="shrink-0 text-[11px] tabular-nums text-neutral-500">
              {checklist.doneCount} of {TOTAL_STEPS}
            </span>
          </div>

          {/* `role="list"`, because Tailwind Preflight sets `list-style: none` and Safari/
              VoiceOver then drop list semantics — including the positions this list conveys. */}
          <ol role="list" className="mt-3 border-t border-neutral-100 px-3 pb-1 pt-2">
            {checklist.steps.map((step, index) => (
              <StepRow
                key={step.id}
                step={step}
                steps={checklist.steps}
                ordinal={index + 1}
                portfolioId={portfolioId}
                isLast={index === checklist.steps.length - 1}
                onNavigate={() => setOpen(false)}
              />
            ))}
          </ol>

          <div className="border-t border-neutral-100 px-3 pb-2.5 pt-2">
            <span className="block text-[11px] leading-[14px] text-neutral-500">
              {checklist.allComplete ? "You're all set" : 'Been here before?'}
            </span>
            {/* The one escape, and it is permanent for this team in this browser — the copy
                promises nothing about bringing it back, because `retirement.ts` cannot. */}
            <Button
              variant="ghost"
              size="sm"
              data-testid="setup-checklist-dismiss"
              className={cn(
                'mt-0.5 h-7 w-full justify-start px-2 text-[12px] text-neutral-500 ring-offset-0 hover:bg-neutral-50 hover:text-neutral-900',
                FOCUS_RING,
              )}
              onClick={hide}
            >
              Don&apos;t show this again
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/**
 * Three segments, one per step, not a continuous bar the data cannot justify. Green is the
 * app's affirmative accent (sidebar active icon, `FOCUS_RING`, `Button variant="success"`);
 * the visible "n of 3" beside it carries the state, so the meter needn't pass contrast alone.
 */
function Meter({ doneCount, className, testId }: { doneCount: number; className?: string; testId?: string }) {
  return (
    <span aria-hidden data-testid={testId} className={cn('flex gap-[3px]', className)}>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          data-filled={index < doneCount}
          className={cn(
            'h-1 flex-1 rounded-full transition-colors duration-500 ease-out motion-reduce:transition-none',
            index < doneCount ? 'bg-green-400' : 'bg-neutral-200',
          )}
        />
      ))}
    </span>
  );
}

/** The meter curved, so the 76px rail shows the same instrument the panel does. */
function SegmentedRing({ doneCount, allComplete }: { doneCount: number; allComplete: boolean }) {
  return (
    <span aria-hidden className="relative flex h-8 w-8 items-center justify-center">
      <svg viewBox="0 0 32 32" className="absolute inset-0 h-8 w-8 -rotate-90">
        {[0, 1, 2].map((index) => (
          // `pathLength={3}` normalises the circumference to one unit per step, so the dash
          // maths reads as counting rather than as trigonometry.
          <circle
            key={index}
            cx="16"
            cy="16"
            r="13"
            fill="none"
            strokeWidth="2.5"
            pathLength={3}
            strokeDasharray="0.88 2.12"
            strokeDashoffset={-index}
            data-filled={index < doneCount}
            className={cn(
              'transition-[stroke] duration-500 ease-out motion-reduce:transition-none',
              index < doneCount ? 'stroke-green-400' : 'stroke-neutral-200',
            )}
          />
        ))}
      </svg>
      {allComplete ? (
        <Check className="relative h-3.5 w-3.5 text-green-400" strokeWidth={3} />
      ) : (
        <span className="relative text-[10px] font-medium tabular-nums text-neutral-500">{doneCount}</span>
      )}
    </span>
  );
}

function StepRow({
  step,
  steps,
  ordinal,
  portfolioId,
  isLast,
  onNavigate,
}: {
  step: ChecklistStep;
  steps: readonly ChecklistStep[];
  ordinal: number;
  portfolioId: number | null;
  isLast: boolean;
  onNavigate: () => void;
}) {
  const { to, blocked } = rowAction(step, steps, portfolioId);
  const turn = stepTurn(step, steps);
  const done = step.status === 'done';
  const { setHasUnsavedChanges, handleNavigation } = useUnsavedChanges();
  const history = useHistory();
  const statusId = `setup-checklist-status-${step.id}`;
  const subtitle = (blocked && STEP_PREREQUISITE[step.id]) || STEP_SUBTITLE[step.id];

  const style = TURN_STYLE[turn];

  const body = (
    <>
      <StepMarker ordinal={ordinal} turn={turn} blocked={blocked} />
      <span className="min-w-0 flex-1">
        {/* Three weights for three places in the run: the turn is loudest, done recedes. */}
        <span className={cn('block text-[13px] leading-[17px]', blocked ? BLOCKED_STYLE.label : style.label)}>
          {STEP_LABEL[step.id]}
        </span>
        {/* aria-hidden, and repeated in the described-by span below: left readable it would
            join the link's accessible name, so voice control would need the whole sentence. */}
        <span aria-hidden className={cn('mt-0.5 block text-[11px] leading-[14px]', style.subtitle)}>
          {subtitle}
        </span>
      </span>
      {to ? (
        <ChevronRight
          aria-hidden
          // Ink rather than green on both the turn and the hover: green is reserved for a step
          // that is finished, and a chevron is an invitation to start one.
          className={cn(
            'mt-[3px] h-3.5 w-3.5 shrink-0 transition-transform group-hover/row:translate-x-0.5 group-hover/row:text-neutral-900 motion-reduce:transition-none',
            turn === 'next' ? 'text-neutral-700' : 'text-neutral-300',
          )}
        />
      ) : null}
    </>
  );

  return (
    <li data-testid={`setup-checklist-row-${step.id}`} className="relative">
      {isLast ? null : (
        /*
         * One unbroken rail through the marker centres. `-bottom-2` spans the next row's own
         * padding, and `z-10` is what keeps it visible there: the rows are `position:relative`
         * siblings, so without it the next row's resting fill paints over the overhang and the
         * rail snaps shut exactly above the turn. Offsets track `px-2`/`py-2` in rem, not 16px.
         */
        <span
          aria-hidden
          data-testid={`setup-checklist-spine-${step.id}`}
          data-travelled={done}
          className={cn(
            'absolute -bottom-2 left-[calc(0.5rem+8px)] top-[calc(0.5rem+18px)] z-10 w-0 border-l-2',
            done ? 'border-green-400' : 'border-neutral-200',
          )}
        />
      )}
      {/* Referenced rather than merely adjacent: a sibling span never reaches a keyboard user
          tabbing the panel, who hears only the link's own name. */}
      <span id={statusId} className="sr-only">
        {`Step ${ordinal} of ${TOTAL_STEPS}. ${blocked ? 'Waiting' : TURN_TEXT[turn]}. ${subtitle}.`}
      </span>
      {to ? (
        <Link
          to={to}
          aria-describedby={statusId}
          // Every other row in this rail routes through the guard (`SidebarItem`), and these
          // now sit among them: a click must not discard an unsaved form.
          onClick={(event) => {
            event.preventDefault();
            onNavigate();
            handleNavigation(() => {
              setHasUnsavedChanges(false);
              history.push(to);
            });
          }}
          className={cn(
            'group/row flex items-start gap-2 rounded-md px-2 py-2 transition-colors focus-visible:outline-none',
            ROW_HOVER,
            FOCUS_RING,
          )}
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-start gap-2 px-2 py-2">{body}</div>
      )}
    </li>
  );
}

/** Two shapes: a filled disc for finished, a dashed ring for everything still to do. */
function StepMarker({ ordinal, turn, blocked }: { ordinal: number; turn: StepTurn; blocked: boolean }) {
  const done = turn === 'done';
  return (
    <span
      aria-hidden
      data-status={done ? 'done' : blocked ? 'blocked' : 'idle'}
      data-turn={turn}
      className={cn(
        'relative flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border text-[10px] font-medium tabular-nums leading-none',
        // White on green matches `Button variant="success"`. It is 2.23:1, which the FILLED disc
        // covers: the shape is what says done, and the row carries "Done" in its status span.
        blocked ? BLOCKED_STYLE.marker : TURN_STYLE[turn].marker,
      )}
    >
      {done ? <Check className="h-3 w-3" strokeWidth={3} /> : ordinal}
    </span>
  );
}

export default SetupChecklistSection;
